import { supabase } from '../lib/clients/supabase';

/**
 * Fetches all gift cards with their descriptions, grouped by vehicle type
 * @returns {Promise<{data: Array, error: Object}>}
 */
export const fetchAllGiftCards = async () => {
  try {
    // First, fetch all gift cards with their related data
    const { data: giftCardsData, error: giftCardsError } = await supabase
      .from('gift_cards')
      .select(`
        id,
        price,
        note,
        created_at,
        updated_at,
        vehicle_types:vehicle_type_id (
          id,
          title
        ),
        services:service_id (
          id,
          name
        ),
        gift_card_descriptions!gift_card_descriptions_gift_card_id_fkey (
          id,
          description
        )
      `)
      .order('created_at', { ascending: true });

    if (giftCardsError) throw giftCardsError;

    // Transform the data to match the expected format in the component
    const transformedData = [];
    const vehicleTypeMap = new Map();

    // Group by vehicle type
    giftCardsData.forEach(card => {
      const vehicleTypeId = card.vehicle_types.id;
      const vehicleTypeTitle = card.vehicle_types.title;
      
      if (!vehicleTypeMap.has(vehicleTypeId)) {
        vehicleTypeMap.set(vehicleTypeId, {
          carType: vehicleTypeTitle,
          services: []
        });
      }

      // Format the service data
      const serviceData = {
        id: card.id,
        name: card.services.name,
        price: card.price,
        note: card.note,
        description: card.gift_card_descriptions.map(desc => ({
          name: desc.description
        }))
      };

      // Add the service to the appropriate vehicle type
      vehicleTypeMap.get(vehicleTypeId).services.push(serviceData);
    });

    // Convert the map to an array
    vehicleTypeMap.forEach(value => {
      transformedData.push(value);
    });

    return { data: transformedData, error: null };
  } catch (error) {
    console.error('Error fetching gift cards:', error);
    return {
      data: null,
      error: {
        message: 'Failed to fetch gift cards',
        details: error.message
      }
    };
  }
};

/**
 * Fetches a specific gift card by ID
 * @param {string} giftCardId - The gift card ID
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const fetchGiftCardById = async (giftCardId) => {
  if (!giftCardId) {
    return {
      data: null,
      error: {
        message: 'Gift card ID is required'
      }
    };
  }

  try {
    const { data, error } = await supabase
      .from('gift_cards')
      .select(`
        id,
        price,
        note,
        created_at,
        updated_at,
        vehicle_types:vehicle_type_id (
          id,
          title
        ),
        services:service_id (
          id,
          name
        ),
        gift_card_descriptions!gift_card_descriptions_gift_card_id_fkey (
          id,
          description
        )
      `)
      .eq('id', giftCardId)
      .single();

    if (error) throw error;

    // Transform the data to match the expected format
    const transformedData = {
      id: data.id,
      carType: data.vehicle_types.title,
      service: {
        name: data.services.name,
        price: data.price,
        note: data.note,
        description: data.gift_card_descriptions.map(desc => ({
          name: desc.description
        }))
      }
    };

    return { data: transformedData, error: null };
  } catch (error) {
    console.error('Error fetching gift card:', error);
    return {
      data: null,
      error: {
        message: 'Failed to fetch gift card',
        details: error.message
      }
    };
  }
};
